import { Close } from '@mui/icons-material';
import { Autocomplete, Chip, Stack, TextField } from '@mui/material';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import type { StyleObj } from '../../../@types';
import DateTimeSelect from '../../molecules/DateTimeSelect';
import FormSelect from '../../molecules/FormSelect';

const styles: StyleObj = {
  chip: {
    backgroundColor: 'primary.container',
    border: 0,
    borderRadius: 2,
    fontWeight: 600,
  },
};

const STATUS_OPTIONS = [
  { id: 'true', name: 'Active' },
  { id: 'false', name: 'Inactive' },
];

type StringArrayFilterProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options?: string[];
  freeSolo?: boolean;
};

export const StringArrayFilter = <T extends FieldValues>({
  control,
  name,
  label,
  options = [],
  freeSolo,
}: StringArrayFilterProps<T>) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Autocomplete
        clearIcon={false}
        options={options}
        value={field.value ?? []}
        multiple
        freeSolo={freeSolo}
        onChange={(_event, value) => field.onChange(value)}
        renderValue={(value, getItemProps) =>
          value.map((option: string, index: number) => (
            // Key is already included by getTagProps.
            // eslint-disable-next-line @eslint-react/no-missing-key
            <Chip
              variant='outlined'
              label={option}
              sx={styles.chip}
              deleteIcon={<Close />}
              {...getItemProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField helperText={freeSolo && 'Press enter to add value'} label={label} {...params} />
        )}
      />
    )}
  />
);

export const StatusFilter = <T extends FieldValues>({ control, name }: { control: Control<T>; name: Path<T> }) => (
  <FormSelect control={control} name={name} label='Status' options={STATUS_OPTIONS} />
);

export const CreatedAtFilter = <T extends FieldValues>({
  control,
  fromName,
  toName,
}: {
  control: Control<T>;
  fromName: Path<T>;
  toName: Path<T>;
}) => (
  <Stack direction='row' spacing={2}>
    <DateTimeSelect name={fromName} control={control} label='Date from' disableTimePicker disableFuture fullWidth />
    <DateTimeSelect name={toName} control={control} label='Date to' disableTimePicker fullWidth />
  </Stack>
);
